// src/services/axiosInstance.js
//
// Single shared axios instance used by all service files.
// The request interceptor reads the current PingOne access token on every
// API call. oidc-client-ts's automaticSilentRenew (configured in
// MslConfig.js) keeps that token fresh in the background, so this generally
// just reads an already-valid token rather than triggering a renewal itself.

import axios from "axios";
import { userManager } from "../msalInstance";

// Shared redirect guard (replaces window.__isRedirecting across all service files)
if (!window.__isRedirecting) {
  window.__isRedirecting = false;
}

const BASE_URL = process.env.REACT_APP_BASE_URL_CONFIG;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000,
});

// ─── REQUEST INTERCEPTOR ─────────────────────────────────────────────────────
// Attaches the current PingOne access token before every request.
// oidc-client-ts's automaticSilentRenew normally keeps the stored user's
// token fresh in the background — the signinSilent() call below is a
// fallback for the rare case a request lands in the gap before that
// renewal completes, not the primary refresh mechanism.
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      let user = await userManager.getUser();

      if (!user) {
        // No active session — let the response interceptor handle the 401
        return config;
      }

      if (user.expired) {
        try {
          user = await userManager.signinSilent();
        } catch (renewError) {
          // Refresh failed — session needs real re-authentication.
          if (!window.__isRedirecting) {
            window.__isRedirecting = true;
            localStorage.setItem("sessionExpired", "true");
            const currentPath = window.location.pathname;
            if (currentPath !== "/" && !currentPath.includes("bulkedit")) {
              localStorage.setItem("failedUrl", currentPath);
            }
            await userManager.signinRedirect();
          }
          return config;
        }
      }

      if (user) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
      }
    } catch (error) {
      // For other errors (network, etc.), let the request proceed without
      // a token — the response interceptor will handle the 401.
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── RESPONSE INTERCEPTOR ────────────────────────────────────────────────────
// Handles 401/403 responses. This replaces the duplicated block that
// currently exists in all 5 service files.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && !window.__isRedirecting) {
      const { status } = error.response;

      if (status === 401) {
        window.__isRedirecting = true;
        localStorage.removeItem("Name");
        localStorage.removeItem("Access");
        localStorage.setItem("sessionExpired", "true");
        userManager.removeUser();

        const currentPath = window.location.pathname;
        if (currentPath !== "/" && !currentPath.includes("bulkedit")) {
          localStorage.setItem("failedUrl", currentPath);
        }

        setTimeout(() => {
          window.location.replace("/");
        }, 100);
      } else if (status === 403) {
        window.__isRedirecting = true;
        localStorage.setItem("noAccess", "true");
        localStorage.removeItem("Name");
        localStorage.removeItem("Access");

        setTimeout(() => {
          window.location.replace("/");
        }, 100);
      }
    } else if (error.request) {
      console.error("Network error:", error.message);
    }

    return Promise.reject(error);
  },
);

// ─── HELPER: get decoded user info from current token ────────────────────────
// Used to replace the manual localStorage.getItem("Name") + atob() decode
// pattern in getPiiData (userServices.js).
export const getTokenClaims = async () => {
  const user = await userManager.getUser();
  if (!user) return null;

  // jwtDecode is already a dependency (used in Login.js)
  const { jwtDecode } = await import("jwt-decode");
  return jwtDecode(user.access_token);
};

// ─── HELPER: get current bearer token string ─────────────────────────────────
// Use this wherever a raw "Bearer <token>" string is needed outside of axios
// (e.g. Swagger UI authentication, custom fetch calls, manual API testing).
// Always returns the current stored token — never reads localStorage.
export const getAccessToken = async () => {
  const user = await userManager.getUser();

  if (!user) {
    throw new Error(
      "[getAccessToken] No active PingOne session found. User may not be logged in.",
    );
  }

  return `Bearer ${user.access_token}`;
};

export default axiosInstance;
